import React, { useState } from 'react';
import classnames from 'classnames';
import { Dropdown } from 'react-bootstrap';
import { Activity, ActivityAssociation } from 'v2/models';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import CreateActivityModal from 'v2/common/CreateActivityModal/CreateActivityModal';
import { useDeleteActivity } from 'v2/queries';
import { toast } from 'react-toastify';

interface ActivityItemProps {
  activity: Activity;
  defaultAssociations?: ActivityAssociation[];
  refetch?: () => void;
}

const ActivityItemActions = ({
  activity,
  defaultAssociations,
  refetch,
}: ActivityItemProps) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showEditActivityModal, setShowEditActivityModal] = useState(false);
  const { mutate: deleteActivity, isLoading: isDeleting } = useDeleteActivity();

  const handleDeleteActivity = (id: number) => {
    deleteActivity(id, {
      onError: (error) => {
        const errorMessage =
          typeof error === 'string'
            ? error
            : 'An error occurred while deleting the activity. Please contact support';
        toast.error(errorMessage);
      },
      onSuccess: () => {
        toast.success('Activity deleted successfully');
      },
      onSettled: () => {
        setShowConfirmationModal(false);
        refetch?.();
      },
    });
  };

  if (!activity.is_editable && !activity.is_deletable) {
    return null;
  }

  const closeCreateActivityModal = () => {
    setShowEditActivityModal(false);
    refetch?.();
  };

  return (
    <>
      <Dropdown className="d-inline mx-2">
        <Dropdown.Toggle
          id="dropdown-autoclose-true"
          variant="link"
          className={classnames('p-0')}
          data-cy="activity:activity-item-action-button"
        >
          Actions
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {activity.is_editable && (
            <Dropdown.Item
              onClick={() => setShowEditActivityModal(true)}
              data-cy="activity:activity-item-edit-button"
            >
              Edit
            </Dropdown.Item>
          )}
          {activity.is_deletable && (
            <Dropdown.Item
              onClick={() => setShowConfirmationModal(true)}
              data-cy="activity:activity-item-delete-button"
            >
              Delete
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>

      <ConfirmationModal
        show={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onOk={() => handleDeleteActivity(activity.id)}
        isLoading={isDeleting}
        okBtnText="Delete"
        title="Delete Activity"
      >
        <>Are you sure you want to delete this activity?</>
      </ConfirmationModal>
      {showEditActivityModal && (
        <CreateActivityModal
          defaultAssociations={defaultAssociations}
          activityToEdit={activity}
          onClose={closeCreateActivityModal}
        />
      )}
    </>
  );
};

export default ActivityItemActions;
